import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Skeleton,
  Alert,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as ClockIcon,
  KeyboardArrowUp as ScrollUpIcon
} from '@mui/icons-material';
import { BookOpen, FileText, HelpCircle, Video } from 'lucide-react';
import api from '../../services/api';

/* ─────────────────────────────────────────────────────────────
   FAQ parser — detects Q:/A: pattern and splits into pairs
───────────────────────────────────────────────────────────── */
const parseFaqContent = (content) => {
  if (!content) return null;
  // Must contain at least one Q: and A: (case-insensitive)
  if (!/q\s*:/i.test(content) || !/a\s*:/i.test(content)) return null;

  const pairs = [];
  // Split on lines that start with Q: (case-insensitive), keeping the delimiter
  const blocks = content.split(/\n(?=q\s*:)/i).filter(Boolean);

  for (const block of blocks) {
    // Each block: first line is Q:..., rest starts with A:
    const lines = block.split('\n');
    const qLine = lines[0].replace(/^q\s*:\s*/i, '').trim();
    const aLines = lines.slice(1);
    const aContent = aLines
      .join('\n')
      .replace(/^a\s*:\s*/i, '')
      .trim();

    if (qLine && aContent) {
      pairs.push({ question: qLine, answer: aContent });
    }
  }

  return pairs.length > 0 ? pairs : null;
};

/* ─────────────────────────────────────────────────────────────
   Rich text renderer — handles markdown-lite + Q/A accordions
───────────────────────────────────────────────────────────── */
const renderArticleContent = (article, theme) => {
  if (!article?.content) return null;

  const faqPairs = parseFaqContent(article.content);

  // ── FAQ accordion mode ──────────────────────────────────────
  if (faqPairs) {
    return (
      <Box sx={{ mt: 1 }}>
        {faqPairs.map((pair, idx) => (
          <Accordion
            key={idx}
            defaultExpanded={idx === 0}
            disableGutters
            elevation={0}
            sx={{
              mb: 1.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.3),
              borderRadius: '10px !important',
              overflow: 'hidden',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                borderColor: alpha(theme.palette.warning.main, 0.6),
                boxShadow: `0 0 0 2px ${alpha(theme.palette.warning.main, 0.15)}`
              }
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{ color: theme.palette.warning.main }}
                />
              }
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.06),
                px: 2.5,
                py: 0.5,
                '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 }
              }}
            >
              <HelpCircle
                size={18}
                style={{ color: theme.palette.warning.main, flexShrink: 0 }}
              />
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, lineHeight: 1.5, color: 'text.primary' }}
              >
                {pair.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {pair.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  }

  // ── Formatted plain-text mode ───────────────────────────────
  const lines = article.content.split('\n');
  const elements = [];
  let bulletBuffer = [];

  const flushBullets = (key) => {
    if (bulletBuffer.length === 0) return;
    elements.push(
      <Box key={`bullets-${key}`} component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1.5 }}>
        {bulletBuffer.map((b, i) => (
          <Box
            key={i}
            component="li"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 0.75,
              listStyle: 'none'
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                mt: '9px',
                flexShrink: 0
              }}
            />
            <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
              {b}
            </Typography>
          </Box>
        ))}
      </Box>
    );
    bulletBuffer = [];
  };

  lines.forEach((line, idx) => {
    // H1
    if (/^#\s/.test(line)) {
      flushBullets(idx);
      elements.push(
        <Typography
          key={idx}
          variant="h5"
          sx={{ fontWeight: 800, mt: 3, mb: 1.5, color: 'text.primary' }}
        >
          {line.replace(/^#\s/, '')}
        </Typography>
      );
      return;
    }
    // H2
    if (/^##\s/.test(line)) {
      flushBullets(idx);
      elements.push(
        <Typography
          key={idx}
          variant="h6"
          sx={{ fontWeight: 700, mt: 3, mb: 1.5, color: 'text.primary' }}
        >
          {line.replace(/^##\s/, '')}
        </Typography>
      );
      return;
    }
    // Divider
    if (/^---+$/.test(line.trim())) {
      flushBullets(idx);
      elements.push(<Divider key={idx} sx={{ my: 3 }} />);
      return;
    }
    // Bullet
    if (/^[-•]\s/.test(line)) {
      bulletBuffer.push(line.replace(/^[-•]\s/, ''));
      return;
    }
    // Bold line
    if (/^\*\*.+\*\*$/.test(line.trim())) {
      flushBullets(idx);
      elements.push(
        <Typography
          key={idx}
          variant="body1"
          sx={{ fontWeight: 700, lineHeight: 1.8, mb: 1, color: 'text.primary' }}
        >
          {line.replace(/^\*\*|\*\*$/g, '')}
        </Typography>
      );
      return;
    }
    // Empty line → small spacer
    if (line.trim() === '') {
      flushBullets(idx);
      elements.push(<Box key={idx} sx={{ mb: 1 }} />);
      return;
    }
    // Default body text
    flushBullets(idx);
    elements.push(
      <Typography
        key={idx}
        variant="body1"
        sx={{ lineHeight: 1.8, mb: 1, color: 'text.secondary' }}
      >
        {line}
      </Typography>
    );
  });

  flushBullets('end');
  return <Box>{elements}</Box>;
};

/* ─────────────────────────────────────────────────────────────
   Excerpt helper — strips Q:/A: and truncates to 150 chars
───────────────────────────────────────────────────────────── */
const buildExcerpt = (content, maxLen = 150) => {
  if (!content) return '';
  const clean = content
    .replace(/^[qa]\s*:\s*/gim, '')
    .replace(/^##?\s/gm, '')
    .replace(/^[-•]\s/gm, '')
    .replace(/\*\*/g, '')
    .replace(/---+/g, '')
    .replace(/\n+/g, ' ')
    .trim();
  return clean.length > maxLen ? clean.slice(0, maxLen) + '…' : clean;
};

/* ─────────────────────────────────────────────────────────────
   Category emoji map
───────────────────────────────────────────────────────────── */
const categoryEmoji = {
  'Getting Started': '🚀',
  'Features': '✨',
  'Budgeting': '💰',
  'Troubleshooting': '🔧',
  'Security': '🔒',
  'Billing': '💳',
  'General': 'ℹ️'
};

/* ═════════════════════════════════════════════════════════════
   MAIN COMPONENT
═════════════════════════════════════════════════════════════ */
const HelpCenterPage = () => {
  const theme = useTheme();

  // ── existing state (unchanged) ──────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  // ── new: scroll-to-top in dialog ───────────────────────────
  const [showScrollTop, setShowScrollTop] = useState(false);
  const dialogContentRef = useRef(null);

  const handleDialogScroll = useCallback(() => {
    if (dialogContentRef.current) {
      setShowScrollTop(dialogContentRef.current.scrollTop > 200);
    }
  }, []);

  const scrollDialogToTop = () => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ── static data (unchanged) ─────────────────────────────────
  const categories = [
    'All',
    'Getting Started',
    'Features',
    'Budgeting',
    'Troubleshooting',
    'Security',
    'Billing',
    'General'
  ];

  const types = ['All', 'Article', 'Guide', 'FAQ', 'Video'];

  const typeColors = {
    Article: '#3b82f6',
    Guide: '#10b981',
    FAQ: '#f59e0b',
    Video: '#a855f7'
  };

  const typeIcons = {
    Article: <FileText size={16} />,
    Guide: <BookOpen size={16} />,
    FAQ: <HelpCircle size={16} />,
    Video: <Video size={16} />
  };

  // ── derived counts for stats bar ───────────────────────────
  const faqCount     = articles.filter((a) => a.type === 'FAQ').length;
  const guideCount   = articles.filter((a) => a.type === 'Guide').length;
  const articleCount = articles.filter((a) => a.type === 'Article').length;

  // ── API calls (unchanged) ───────────────────────────────────
  useEffect(() => {
    fetchArticles();
  }, [selectedCategory, selectedType, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'All') params.category = selectedCategory;
      if (selectedType !== 'All') params.type = selectedType;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const response = await api.get('/help', { params });
      if (response.data.success) {
        setArticles(response.data.data.content || []);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleArticleClick = async (articleId) => {
    try {
      const response = await api.get(`/help/${articleId}`);
      if (response.data.success) {
        setSelectedArticle(response.data.data);
        setDialogOpen(true);
        setFeedbackGiven(false);
        setFeedbackMessage('');
        setShowScrollTop(false);
      }
    } catch (error) {
      console.error('Error fetching article:', error);
    }
  };

  const handleFeedback = async (helpful) => {
    if (!selectedArticle || feedbackGiven) return;
    try {
      await api.post(`/help/${selectedArticle._id}/feedback`, { helpful });
      setFeedbackGiven(true);
      setFeedbackMessage('Thanks for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedbackMessage('Failed to submit feedback. Please try again.');
    }
  };

  const calculateReadingTime = (content) => {
    if (!content) return '~1 min read';
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `~${minutes} min read`;
  };

  /* ── render ─────────────────────────────────────────────── */
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 4, pb: 8 }}>
      <Container maxWidth="lg">

        {/* ════════════════════════════════════════════════════
            HERO SECTION
        ════════════════════════════════════════════════════ */}
        <Box sx={{ textAlign: 'center', mb: 6, mt: 2, position: 'relative' }}>

          {/* Decorative radial gradient blob */}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -60%)',
              width: 600,
              height: 320,
              borderRadius: '50%',
              background:
                'radial-gradient(ellipse at center, rgba(124,58,237,0.18) 0%, rgba(59,130,246,0.10) 55%, transparent 80%)',
              filter: 'blur(40px)',
              pointerEvents: 'none',
              zIndex: 0
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 1.5,
                background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Help Center
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Find answers, guides, and tutorials
            </Typography>

            {/* Search Bar */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search help articles…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                maxWidth: 600,
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  transition: 'box-shadow 0.2s ease',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 3px rgba(124,58,237,0.25)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#7c3aed'
                    }
                  }
                }
              }}
            />

            {/* Category Filter Chips */}
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              {categories.map((category) => {
                const isActive = selectedCategory === category;
                const emoji = categoryEmoji[category];
                return (
                  <Chip
                    key={category}
                    label={emoji ? `${emoji} ${category}` : category}
                    onClick={() => setSelectedCategory(category)}
                    sx={{
                      fontWeight: isActive ? 600 : 400,
                      background: isActive
                        ? 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)'
                        : 'transparent',
                      color: isActive ? '#fff' : 'text.primary',
                      border: isActive ? 'none' : '1px solid',
                      borderColor: 'divider',
                      mb: 1,
                      '&:hover': {
                        background: isActive
                          ? 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)'
                          : alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  />
                );
              })}
            </Stack>

            {/* Type Filter Chips */}
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ mb: 3 }}
            >
              {types.map((type) => {
                const isActive = selectedType === type;
                return (
                  <Chip
                    key={type}
                    label={type}
                    onClick={() => setSelectedType(type)}
                    icon={type !== 'All' ? typeIcons[type] : undefined}
                    sx={{
                      fontWeight: isActive ? 600 : 400,
                      background: isActive
                        ? typeColors[type] || 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)'
                        : 'transparent',
                      color: isActive ? '#fff' : 'text.primary',
                      border: isActive ? 'none' : '1px solid',
                      borderColor: 'divider',
                      mb: 1,
                      '& .MuiChip-icon': { color: isActive ? '#fff' : 'inherit' },
                      '&:hover': {
                        background: isActive
                          ? typeColors[type] || 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)'
                          : alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  />
                );
              })}
            </Stack>

            {/* Stats Row */}
            {!loading && articles.length > 0 && (
              <Stack
                direction="row"
                spacing={0.75}
                justifyContent="center"
                alignItems="center"
                flexWrap="wrap"
              >
                {[
                  { label: 'Articles', count: articles.length },
                  { label: 'FAQs', count: faqCount },
                  { label: 'Guides', count: guideCount },
                  { label: 'Tutorials', count: articleCount }
                ].map((stat, i, arr) => (
                  <React.Fragment key={stat.label}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.82rem'
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: 'primary.main',
                          mr: 0.4
                        }}
                      >
                        {stat.count}
                      </Box>
                      {stat.label}
                    </Typography>
                    {i < arr.length - 1 && (
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        •
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            )}
          </Box>
        </Box>

        {/* ════════════════════════════════════════════════════
            ARTICLES GRID
        ════════════════════════════════════════════════════ */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item}>
                <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  <Box sx={{ height: 4, bgcolor: alpha(theme.palette.primary.main, 0.3) }} />
                  <CardContent>
                    <Skeleton variant="rectangular" height={32} sx={{ mb: 2, borderRadius: 1 }} />
                    <Skeleton variant="text" sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="80%" sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="55%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : articles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 12 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                mb: 3
              }}
            >
              <BookOpen
                size={64}
                style={{ color: theme.palette.text.disabled }}
              />
            </Box>
            <Typography variant="h5" color="text.secondary" gutterBottom fontWeight={600}>
              No help articles yet
            </Typography>
            <Typography variant="body1" color="text.disabled" sx={{ maxWidth: 400, mx: 'auto' }}>
              Check back soon — our team is publishing guides to help you get the most out of
              SoldiKeeper.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {articles.map((article) => {
              const accentColor = typeColors[article.type] || theme.palette.primary.main;
              const excerpt = buildExcerpt(article.content);
              const isPopular = (article.views || 0) > 10;
              const tags = article.tags || [];

              return (
                <Grid item xs={12} sm={6} md={4} key={article._id}>
                  <Card
                    onClick={() => handleArticleClick(article._id)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-left-color 0.25s ease',
                      borderLeft: `4px solid ${accentColor}`,
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: theme.shadows[8],
                        borderLeftColor: accentColor === typeColors.FAQ
                          ? '#fbbf24'
                          : accentColor === typeColors.Guide
                          ? '#34d399'
                          : accentColor === typeColors.Article
                          ? '#60a5fa'
                          : '#c084fc'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      {/* Type + Category + Popular badge */}
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5, gap: 0.5 }}>
                        <Chip
                          size="small"
                          icon={typeIcons[article.type]}
                          label={article.type}
                          sx={{
                            bgcolor: accentColor,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            '& .MuiChip-icon': { color: '#fff' }
                          }}
                        />
                        <Chip
                          size="small"
                          label={`${categoryEmoji[article.category] || ''} ${article.category}`}
                          variant="outlined"
                          sx={{ fontSize: '0.72rem' }}
                        />
                        {isPopular && (
                          <Chip
                            size="small"
                            label="🔥 Popular"
                            sx={{
                              bgcolor: alpha('#f97316', 0.15),
                              color: '#f97316',
                              fontWeight: 700,
                              fontSize: '0.70rem',
                              border: '1px solid',
                              borderColor: alpha('#f97316', 0.35)
                            }}
                          />
                        )}
                      </Stack>

                      {/* Title */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 1.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4,
                          fontSize: '1rem'
                        }}
                      >
                        {article.title}
                      </Typography>

                      {/* Excerpt */}
                      {excerpt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1.5,
                            lineHeight: 1.65,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {excerpt}
                        </Typography>
                      )}

                      {/* Tags */}
                      {tags.length > 0 && (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1.5 }}>
                          {tags.slice(0, 2).map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.68rem',
                                height: 20,
                                color: 'text.disabled',
                                borderColor: alpha(theme.palette.divider, 0.8)
                              }}
                            />
                          ))}
                        </Stack>
                      )}

                      {/* Meta row */}
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 'auto' }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <ClockIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {calculateReadingTime(article.content)}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.disabled">•</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <ViewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {article.views || 0}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.disabled">•</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(article.createdAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 2, pt: 0.5 }}>
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<ArrowForwardIcon fontSize="small" />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          color: accentColor,
                          fontSize: '0.85rem',
                          pl: 0,
                          '&:hover': { bgcolor: alpha(accentColor, 0.08) }
                        }}
                      >
                        Read
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* ══════════════════════════════════════════════════════
          ARTICLE DETAIL DIALOG
      ══════════════════════════════════════════════════════ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '90vh', position: 'relative' }
        }}
      >
        {selectedArticle && (
          <>
            {/* Gradient Header */}
            <DialogTitle
              sx={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                color: '#fff',
                position: 'relative',
                pb: 3
              }}
            >
              <IconButton
                onClick={() => setDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
              >
                <CloseIcon />
              </IconButton>

              <Stack spacing={1.5} sx={{ pr: 5 }}>
                {/* Type + category chips */}
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    icon={typeIcons[selectedArticle.type]}
                    label={selectedArticle.type}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: '#fff' }
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${categoryEmoji[selectedArticle.category] || ''} ${selectedArticle.category}`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
                  />
                </Stack>

                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {selectedArticle.title}
                </Typography>

                {/* Meta */}
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    By {selectedArticle.author?.username || 'Admin'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>•</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {new Date(selectedArticle.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>•</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <ClockIcon sx={{ fontSize: 13 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {calculateReadingTime(selectedArticle.content)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>•</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <ViewIcon sx={{ fontSize: 13 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {selectedArticle.views || 0} views
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </DialogTitle>

            {/* Scrollable content area */}
            <DialogContent
              ref={dialogContentRef}
              onScroll={handleDialogScroll}
              sx={{ pt: 3, pb: 2 }}
            >
              {/* Tags row */}
              {selectedArticle.tags?.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2.5 }}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontWeight: 600, alignSelf: 'center', mr: 0.5 }}
                  >
                    Tags:
                  </Typography>
                  {selectedArticle.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.72rem',
                        color: 'text.secondary',
                        borderColor: 'divider'
                      }}
                    />
                  ))}
                </Stack>
              )}

              {/* Smart content renderer */}
              {renderArticleContent(selectedArticle, theme)}

              <Divider sx={{ my: 4 }} />

              {/* Feedback Section */}
              <Box sx={{ textAlign: 'center', pb: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Was this helpful?
                </Typography>
                {feedbackMessage ? (
                  <Alert
                    severity={feedbackMessage.includes('Thanks') ? 'success' : 'error'}
                    sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}
                  >
                    {feedbackMessage}
                  </Alert>
                ) : (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    sx={{ mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<ThumbUpIcon />}
                      onClick={() => handleFeedback(true)}
                      disabled={feedbackGiven}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: 120
                      }}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ThumbDownIcon />}
                      onClick={() => handleFeedback(false)}
                      disabled={feedbackGiven}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: 120,
                        borderColor: 'error.main',
                        color: 'error.main',
                        '&:hover': { borderColor: 'error.dark', bgcolor: alpha(theme.palette.error.main, 0.06) }
                      }}
                    >
                      No
                    </Button>
                  </Stack>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={() => setDialogOpen(false)}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)'
                  }
                }}
              >
                Back to Help Center
              </Button>
            </DialogActions>

            {/* Scroll-to-top FAB inside dialog */}
            {showScrollTop && (
              <Fab
                size="small"
                onClick={scrollDialogToTop}
                sx={{
                  position: 'absolute',
                  bottom: 72,
                  right: 16,
                  bgcolor: alpha(theme.palette.primary.main, 0.9),
                  color: '#fff',
                  boxShadow: theme.shadows[4],
                  '&:hover': { bgcolor: theme.palette.primary.main }
                }}
              >
                <ScrollUpIcon />
              </Fab>
            )}
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default HelpCenterPage;
